/**
 * Zoom-style timezone labels: (GMT-07:00) Mountain Time
 * Offsets and generic names follow Intl and change with DST for the given instant.
 *
 * @see https://developers.zoom.us/docs/api/rest/other-references/abbreviation-lists/#timezones
 * (IANA IDs; display pattern matches common scheduling UIs.)
 */

/**
 * @param {string} longOffsetPart e.g. "GMT-7", "GMT-07:00", "GMT+5:30"
 * @returns {number} minutes offset from UTC (east positive, west negative)
 */
export function parseLongOffsetToMinutes(longOffsetPart) {
  if (!longOffsetPart) return 0
  const s = String(longOffsetPart).replace(/\u2212/g, '-').trim()
  const m = s.match(/GMT\s*([+-])(\d{1,2})(?::(\d{2}))?/i)
  if (!m) return 0
  const sign = m[1] === '-' ? -1 : 1
  const h = parseInt(m[2], 10)
  const min = m[3] != null && m[3] !== '' ? parseInt(m[3], 10) : 0
  return sign * (h * 60 + min)
}

/**
 * @param {number} offsetMinutes east positive, west negative (inverse of Date.getTimezoneOffset)
 */
export function formatGmtParenLabel(offsetMinutes) {
  if (offsetMinutes === 0) return '(GMT+00:00)'
  const sign = offsetMinutes < 0 ? '-' : '+'
  const abs = Math.abs(offsetMinutes)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `(GMT${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')})`
}

/**
 * @param {string} iana IANA time zone ID
 * @param {Date} [atDate]
 * @returns {{ value: string, label: string, offsetMinutes: number, genericName: string, gmtParen: string }}
 */
export function getTimezoneLabelParts(iana, atDate = new Date()) {
  const d = atDate instanceof Date && !Number.isNaN(atDate.getTime()) ? atDate : new Date()

  let offsetMinutes = 0
  let longOffset = ''
  try {
    const f = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'longOffset',
    })
    const parts = f.formatToParts(d)
    longOffset = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
    offsetMinutes = parseLongOffsetToMinutes(longOffset)
  } catch {
    offsetMinutes = 0
  }

  let generic = ''
  try {
    const f2 = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'longGeneric',
    })
    const parts2 = f2.formatToParts(d)
    generic = parts2.find((p) => p.type === 'timeZoneName')?.value?.trim() ?? ''
  } catch {
    generic = ''
  }

  if (!generic) {
    generic = iana.replace(/_/g, ' ')
  }

  if (iana === 'Etc/UTC' || iana === 'UTC') {
    generic = 'UTC'
  }

  const gmtParen = formatGmtParenLabel(offsetMinutes)
  const label = `${gmtParen} ${generic}`
  return {
    value: iana,
    label,
    offsetMinutes,
    genericName: generic,
    gmtParen,
  }
}

let cachedOptions = null
let cachedHourKey = null

/**
 * Full IANA list with Zoom-style labels, sorted by current UTC offset then name.
 * Cache is refreshed hourly so DST transitions are reflected without a full page reload.
 *
 * @param {Date} [atDate]
 * @returns {Array<{ value: string, label: string, offsetMinutes: number, searchText: string }>}
 */
export function getAllTimezoneOptions(atDate = new Date()) {
  const d = atDate instanceof Date && !Number.isNaN(atDate.getTime()) ? atDate : new Date()
  const hourKey = Math.floor(d.getTime() / 3600000)
  if (cachedOptions && cachedHourKey === hourKey) {
    return cachedOptions
  }

  let zones = []
  try {
    zones = Intl.supportedValuesOf('timeZone')
  } catch {
    zones = []
  }

  const rows = zones.map((z) => {
    const parts = getTimezoneLabelParts(z, d)
    const searchText = `${z} ${parts.label} ${parts.genericName}`.toLowerCase()
    return {
      value: z,
      label: parts.label,
      offsetMinutes: parts.offsetMinutes,
      searchText,
    }
  })

  rows.sort((a, b) => {
    if (a.offsetMinutes !== b.offsetMinutes) return a.offsetMinutes - b.offsetMinutes
    return a.label.localeCompare(b.label, 'en')
  })

  cachedOptions = rows
  cachedHourKey = hourKey
  return rows
}

/** Invalidate hourly cache (e.g. after long idle tab). */
export function refreshTimezoneOptionsCache() {
  cachedOptions = null
  cachedHourKey = null
}

/**
 * @param {string} query
 * @param {Date} [atDate]
 */
export function filterTimezoneOptions(query, atDate = new Date()) {
  const q = query.trim().toLowerCase()
  const all = getAllTimezoneOptions(atDate)
  if (!q) return all
  return all.filter((row) => row.searchText.includes(q))
}
